// obfuscate ip addresses which should not be stored long-term.

import * as SDPUtils from 'sdp';
import ipaddr from 'ipaddr.js';

// a compliant mDNS address is a UUID + '.local' but Webkit appends a counter
// after the UUID
const MDNS_REGEX = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12,}\.local$/;

// Firefox 70 starts passing (redacted) sometimes
// https://hg.mozilla.org/integration/autoland/rev/3c4fb056f40c#l1.74
const RARE_REGEX = /^\(redacted\)$/;

// obfuscate ip, keeping address family intact.
export const obfuscateIP = (addrString) => {
  // if this is a multicast dns address, this is already obfuscated so we just
  // return as is
  if (MDNS_REGEX.test(addrString) || RARE_REGEX.test(addrString)) {
    return addrString;
  }

  let addr;
  try {
    addr = ipaddr.process(addrString);
  } catch (error) {
    // We're interested in what address couldn't be parsed but
    // ipaddr's error doesn't contain the address itself.
    throw new Error(`ipaddr couldn't parse ${addrString}, original error: ${error.message}`);
  }

  const kind = addr.kind();
  const raw  = addr.toByteArray();
  switch (kind) {
    case 'ipv4':
      // reduce resolution to /24, assuming customer allocation of /32
      raw[3] = 0;
      return new ipaddr.IPv4(raw).toString();
    case 'ipv6':
      // reduce resolution to /40, assuming customer allocation of /48
      // Actually /56 is a typical allocation, but /48 is also a reasonable choice.
      // Let's error on the side of privacy.
      for (let octet = 5; octet < 16; octet++) {
        raw[octet] = 0;
      }
      return new ipaddr.IPv6(raw).toNormalizedString();
    default:
      throw new Error(`Unknown ip address kind: '${kind}'`);
  }
};

// obfuscate the ip in ice candidates. Does NOT obfuscate the ip of the TURN server to allow
// selecting/grouping sessions by TURN server.
export const obfuscateCandidate = function (candidate) {
  const cand = SDPUtils.parseCandidate(candidate);
  if (cand.type !== 'relay') {
    cand.ip = obfuscateIP(cand.ip);
    cand.address = obfuscateIP(cand.address);
  }
  if (cand.relatedAddress) {
    cand.relatedAddress = obfuscateIP(cand.relatedAddress);
  }
  return SDPUtils.writeCandidate(cand);
};

export const obfuscateSDP = function (sdp) {
  const lines = SDPUtils.splitLines(sdp);
  return `${lines.map(function (line) {
    // obfuscate a=candidate, c= and a=rtcp
    if (line.indexOf('a=candidate:') === 0) {
      return obfuscateCandidate(line);
    } else if (line.indexOf('c=') === 0) {
      return 'c=IN IP4 0.0.0.0';
    } else if (line.indexOf('a=rtcp:') === 0) {
      return 'a=rtcp:9 IN IP4 0.0.0.0';
    } else {
      return line;
    }
  }).join('\r\n').trim()}\r\n`;
};

export const obfuscateStats = function (stats) {
  Object.keys(stats).forEach(function (id) {
    const report = stats[id];
    if (report.candidateType && !['relay', 'relayed'].includes(report.candidateType)) {
      // Firefox 69 uses "address"
      // Chrome 77 uses "ip"
      // "ipAddress" is from the original rtcstats code
      ['ip', 'ipAddress', 'address'].forEach((name) => {
        if (report[name]) {
          report[name] = obfuscateIP(report[name]);
        }
      });
    }
    ['googLocalAddress', 'googRemoteAddress'].forEach(function (name) {
      // contains both address and port
      const address = report[name];
      if (address) {
        const portSeperator = address.lastIndexOf(':');
        let ip = address.substr(0, portSeperator);
        const port = address.substr(portSeperator + 1);
        if (ip.startsWith('[') && ip.endsWith(']')) {
          const innerIP = ip.substr(1, ip.length - 2);
          ip = `[${obfuscateIP(innerIP)}]`;
        } else {
          ip = obfuscateIP(ip);
        }
        report[name] = `${ip}:${port}`;
      }
    });
  });
};

const deepClone = (x) => x && JSON.parse(JSON.stringify(x));

export default (eventName, detailsToObfuscate) => {
  const details = deepClone(detailsToObfuscate);
  switch (eventName) {
    case 'addIceCandidate':
    case 'onicecandidate':
      if (details && details.candidate) {
        details.candidate = obfuscateCandidate(details.candidate);
      }
      break;
    case 'setLocalDescription':
    case 'setRemoteDescription':
    case 'createOfferOnSuccess':
    case 'createAnswerOnSuccess':
      if (details && details.sdp) {
        details.sdp = obfuscateSDP(details.sdp);
      }
      break;
    case 'getStats':
    case 'getstats':
      if (details) {
        obfuscateStats(details);
      }
      break;
    default:
      break;
  }
  return details;
};
